import React, { useEffect, useReducer, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { RowForm } from 'pages/Login/style';
import {
  FORGOT_PASSWORD_RESEND_OTP,
  FORGOT_PASSWORD_SUBMIT
} from 'redux/sagas/actions/authentication';
import {
  FORGOT_VERIFICATION_CODE,
  RESET_ERRORS,
  SET_ACCESS_ERROR_OTP,
  SET_ACCESS_ERROR_PASSWORD_NOT_MATCH,
  SET_ACCESS_ERROR_PASSWORD_NOT_VALID
} from 'redux/slices/authentication';
import { Button, Input, Tooltip } from 'ui/atoms';
import MainFont from 'ui/typography/mainFont';
import validator from 'utils/regex-validator';

import {
  ForgotPasswordText,
  ForgotPasswordTitle,
  InsertEmailTextWrapper,
  ResendCode,
  SendOTPText,
  SpinnerContainer,
  SubmitWrapper
} from './style';
import spinnerIcon from '../../ui/assets/img/spinner-icon-white.png';
import { SpinnerIcon } from '../../ui/atoms/Table/style';

const reducer = (state, action) => {
  switch (action.type) {
    case 'add_password':
      return {
        ...state,
        password: action.payload
      };
    case 'add_confirm_password':
      return {
        ...state,
        confirm_password: action.payload
      };
    case 'code':
      return {
        ...state,
        code: action.payload
      };
    default:
      return state;
  }
};

const newField = () => '';

const initialState = {
  confirm_password: newField(),
  password: newField(),
  code: newField()
};

const ChangePassword = ({
  setErrorPswNotValid,
  setErrorOtp,
  setErrorPswDontMatch,
  forgotPasswordSubmit,
  resendVerificationCode,
  saveVerificationCode,
  resetErrors,
  errorOtp,
  errorPswNotValid,
  errorPswDontMatch,
  isLoading,
  resendOtp
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { t } = useTranslation();
  const [formattedPasswordErrors, setFormattedPassordErrors] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleValidation = () => {
    if (errorOtp || errorPswNotValid || errorPswDontMatch) {
      return false;
    }
    resetErrors();
    return true;
  };

  const handleSubmitPassword = () => {
    if (handleValidation()) {
      const { code, password } = state;
      saveVerificationCode(code);
      forgotPasswordSubmit(password);
    }
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      handleSubmitPassword();
    }
  };

  useEffect(() => {
    if (state.code && state.code.length !== 0) {
      if (!validator.atecoCode(state.code)) {
        setErrorOtp(t('code_not_valid'));
      } else {
        setErrorOtp(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.code]);

  useEffect(() => {
    if (
      state.password &&
      state.confirm_password &&
      state.password !== state.confirm_password
    ) {
      setErrorPswDontMatch(t('password_not_match'));
    } else {
      setErrorPswDontMatch(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.password, state.confirm_password]);

  useEffect(() => {
    if (state.password && !validator.password(state.password)) {
      setErrorPswNotValid(t('password_not_valid'));
    } else {
      setErrorPswNotValid(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.password]);

  useEffect(() => {
    if (errorPswNotValid && errorPswDontMatch) {
      if (errorPswDontMatch.toLowerCase().includes('confirm')) {
        setFormattedPassordErrors(
          `<ul><li>${t('password_not_match')}</li><li>${t(
            'confirm_psw_not_valid'
          )}</li></ul>`
        );
      } else {
        setFormattedPassordErrors(
          `<ul><li>${t('password_not_match')}</li><li>${t(
            'psw_not_valid'
          )}</li></ul>`
        );
      }
    } else if (errorPswDontMatch && !errorPswNotValid) {
      setFormattedPassordErrors(errorPswDontMatch);
    } else if (errorPswNotValid && !errorPswDontMatch) {
      setFormattedPassordErrors(errorPswNotValid);
    } else if (!errorPswNotValid && !errorPswDontMatch) {
      setFormattedPassordErrors('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorPswNotValid, errorPswDontMatch]);

  const isDisabled = () =>
    !state.password || !state.confirm_password || !state.code;

  return (
    <>
      <ForgotPasswordTitle>
        <MainFont configuration={{ fontSize: 24 }} type="3">
          {t('create_new_password')}
        </MainFont>
      </ForgotPasswordTitle>

      <div onKeyPress={handleKeyPress}>
        <InsertEmailTextWrapper
          data-tip={`${t(errorOtp)}`}
          data-for="otp-tooltip"
        >
          {!isLoading ? (
            <Input
              value={state.code}
              placeholder={`${t('insert_the_code')}`}
              variant="otp"
              onChange={e =>
                dispatch({
                  type: 'code',
                  payload: e.currentTarget.value
                })
              }
              status={errorOtp ? 'invalid' : ''}
            />
          ) : (
            <SpinnerContainer>
              <SpinnerIcon icon={spinnerIcon} />
            </SpinnerContainer>
          )}
        </InsertEmailTextWrapper>
        {errorOtp && <Tooltip id="otp-tooltip" error />}
        <SendOTPText>
          <MainFont configuration={{ fontSize: 12 }} type="3">
            {t('otp_message')}
          </MainFont>
          <ResendCode
            onClick={() => {
              resendOtp();
            }}
            isLoading={isLoading}
          >
            <MainFont configuration={{ fontSize: 14 }} type="4">
              {t('resend')}
            </MainFont>
          </ResendCode>
        </SendOTPText>
        <ForgotPasswordText>
          <MainFont configuration={{ fontSize: 14 }} type="3">
            {t('create_password_text')}
          </MainFont>
        </ForgotPasswordText>

        <RowForm>
          <Input
            type="password"
            placeholder={`${t('new_password')}`}
            variant="login"
            value={state.password}
            onChange={e =>
              dispatch({
                type: 'add_password',
                payload: e.currentTarget.value
              })
            }
            isShowPassword
            data-tip={`${formattedPasswordErrors}`}
            data-for="psw-tooltip"
            data-html
            status={formattedPasswordErrors ? 'invalid' : ''}
          />
          {formattedPasswordErrors && <Tooltip id="psw-tooltip" error />}
        </RowForm>
        <RowForm>
          <Input
            type="password"
            placeholder={`${t('confirm_password')}`}
            variant="login"
            value={state.confirm_password}
            onChange={e =>
              dispatch({
                type: 'add_confirm_password',
                payload: e.currentTarget.value
              })
            }
            isShowPassword
            status={formattedPasswordErrors ? 'invalid' : ''}
          />
        </RowForm>
      </div>
      <SubmitWrapper>
        <Button
          variant="otp"
          disabled={isDisabled()}
          onClick={handleSubmitPassword}
        >
          <MainFont configuration={{ fontSize: 14 }} type="4">
            {t('submit')}
          </MainFont>
        </Button>
      </SubmitWrapper>
    </>
  );
};

ChangePassword.propTypes = {
  forgotPasswordSubmit: PropTypes.func.isRequired,
  setErrorPswDontMatch: PropTypes.func.isRequired,
  setErrorPswNotValid: PropTypes.func.isRequired,
  resetErrors: PropTypes.func.isRequired,
  setErrorOtp: PropTypes.func.isRequired,
  resendVerificationCode: PropTypes.func,
  saveVerificationCode: PropTypes.func,
  errorOtp: PropTypes.string,
  errorPswDontMatch: PropTypes.string,
  errorPswNotValid: PropTypes.string,
  isLoading: PropTypes.bool,
  resendOtp: PropTypes.func
};

export default connect(
  ({ authentication }) => ({
    errorGeneric: authentication.errorMessage,
    errorOtp: authentication.errorOtp,
    errorPswDontMatch: authentication.errorPswDontMatch,
    errorPswNotValid: authentication.errorPswNotValid,
    isLoading: authentication.isLoading
  }),
  dispatch => ({
    setErrorPswDontMatch: payload =>
      dispatch(SET_ACCESS_ERROR_PASSWORD_NOT_MATCH(payload)),
    setErrorPswNotValid: payload =>
      dispatch(SET_ACCESS_ERROR_PASSWORD_NOT_VALID(payload)),
    setErrorOtp: payload => dispatch(SET_ACCESS_ERROR_OTP(payload)),
    forgotPasswordSubmit: payload =>
      dispatch({ type: FORGOT_PASSWORD_SUBMIT._REQUEST, payload }),
    saveVerificationCode: payload =>
      dispatch(FORGOT_VERIFICATION_CODE(payload)),
    resetErrors: () => dispatch(RESET_ERRORS()),
    resendOtp: () => dispatch({ type: FORGOT_PASSWORD_RESEND_OTP._REQUEST })
  })
)(ChangePassword);
